import { Component, computed, effect, inject, signal } from "@angular/core";
import { TranslocoModule } from "@jsverse/transloco";
import { JourneyBreadcrumb } from "../../common/journey-breadcrumb/journey-breadcrumb";
import { JourneySearchStore } from "src/app/features/store/journey-search-store";
import { Router } from "@angular/router";
import { Button } from "src/app/shared/components/utils/button/button";
import { Icon } from "src/app/shared/components/utils/icon/icon";
import {
  TileInterface,
  TileStatusEnum,
} from "src/app/core/interfaces/tile-interface";
import { SafeHtmlPipe } from "../../../../../shared/pipes/safe-html.pipe";
import { ProgressionBar } from "../../common/progression-bar/progression-bar";
import { LanguageService } from "src/app/core/services/language-service";
import { Loader } from "src/app/shared/components/loader/loader";
import { Toggle } from "src/app/shared/components/toggle/toggle";
import { UserStore } from "src/app/features/store/user-store";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";
import { Modal } from "src/app/shared/components/utils/modal/modal";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";

@Component({
  selector: "app-language-test-page",
  imports: [
    TranslocoModule,
    JourneyBreadcrumb,
    Button,
    Icon,
    SafeHtmlPipe,
    ProgressionBar,
    Loader,
    Toggle,
    Modal,
  ],
  templateUrl: "./language-test-page.html",
})
export class LanguageTestPage {
  tilesApi = inject(TilesApiService);

  store = inject(JourneySearchStore);
  userStore = inject(UserStore);
  tilesLanguageTest = signal<TileInterface[] | undefined>(undefined);
  userRole = computed(() => this.userStore.getUserPermission());

  tilesLanguageTestToShow = computed(() => {
    const sourceTiles = this.modeDraft()
      ? this.tilesLanguageTest()
      : this.tilesLanguageTest().filter(
          (tile) => tile.status === TileStatusEnum.Approved
        );
    return sourceTiles.map((tile, index) => ({
      ...tile,
      size: this.tilesApi.giveSizeToTiles(index),
    }));
  });

  router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly piano = inject(PianoAnalyticsService);

  tilesLoading = computed(() => this.store.tilesLoading());

  modeDraft = signal<boolean>(false);

  isModalOpen = signal<boolean>(false);

  isRedirectionExternal = signal<boolean>(false);

  effectiveTextDisclaimer = signal<string>("");

  currentTileToRedirect = signal<TileInterface>(undefined);

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      this.reloadProjectTiles();
    });
  }

  private async reloadProjectTiles(): Promise<void> {
    await this.store.loadTilesByTheme();
    this.tilesLanguageTest.set(this.store.tilesLanguageTest());
    if (!this.store.isJourneyImmature()) {
      await this.store.loadVideos();
    }
  }

  redirectToJourneyResearch(): void {
    this.router.navigate(["/journey/search"]);
  }

  clickOnTile(questionnaireTile: TileInterface) {
    this.isRedirectionExternal.set(questionnaireTile.isExternalLink);
    this.currentTileToRedirect.set(questionnaireTile);
    if (questionnaireTile.isExternalLink) {
      if (this.tilesApi.hasMeaningfulTextContent(questionnaireTile.textDisclaimer)) {
        this.effectiveTextDisclaimer.set(questionnaireTile.textDisclaimer);
      } else {
        this.effectiveTextDisclaimer.set(undefined);
      }
      this.isModalOpen.set(true);
    } else {
      this.goToLink(questionnaireTile);
    }
  }

  toggleDraftMode(): void {
    this.modeDraft.set(!this.modeDraft());
  }

  closeModal(): void {
    this.currentTileToRedirect.set(undefined);
    this.isRedirectionExternal.set(false);
    this.isModalOpen.set(false);
  }

  goToLinkForExternalRedirection(): void {
    const tileToRedirect = this.currentTileToRedirect();
    const link = tileToRedirect.ctaLink;
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
    this.piano.sendEvent("tiles.selection", {
      tile_name: tileToRedirect.title,
    });
    if (tileToRedirect.isExternalLink) {
      this.closeModal();
    }
  }

  goToLink(questionnaireTile: TileInterface): void {
    const link = questionnaireTile?.ctaLink;
    if (link) {
      this.router.navigate([link]);
    }
    this.piano.sendEvent("tiles.selection", {
      tile_name: questionnaireTile.title,
    });
    if (questionnaireTile.isExternalLink) {
      this.closeModal();
    }
  }
}
